import React from 'react';
import { Card, Group, Text, Button, Box, useMantineTheme } from '@mantine/core';
import { IconBook } from '@tabler/icons-react';
import { useMediaQuery } from '@mantine/hooks';
import { Recipe } from '../../types/mealPlan';
import { RecipeImage } from '../UI/RecipeImage';
import { getPageCache, setPageCache } from '../../utils/pageCache';
import { formatCount } from '../../utils/formatCount';

type PopulatedCookbookCTAProps = {
  /** A sample of recent recipes for the collage (not the whole cookbook). */
  recipes: Recipe[];
  /** Total saved recipes — the sample's length is not the cookbook size. */
  recipeCount: number;
  onViewCookbook: () => void;
};

const CTA_PICKS_CACHE_KEY = 'cookbook-cta-picks';

export const PopulatedCookbookCTA: React.FC<PopulatedCookbookCTAProps> = ({
  recipes,
  recipeCount,
  onViewCookbook
}) => {
  const theme = useMantineTheme();
  const isDesktop = useMediaQuery(`(min-width: ${theme.breakpoints.md})`);
  const isMobile = useMediaQuery(`(max-width: ${theme.breakpoints.sm})`);

  // Shuffle once per session and remember the picked ids, so a remount (back
  // navigation) or a background refresh of the same list keeps showing the
  // same collage instead of swapping photos. A fresh session re-rolls, which
  // keeps the intended visit-to-visit variety.
  const selectedRecipes = React.useMemo(() => {
    if (recipes.length <= 3) return recipes;

    const pickedIds = getPageCache<string[]>(CTA_PICKS_CACHE_KEY);
    if (pickedIds) {
      const byId = new Map(recipes.map(r => [r.id, r]));
      const picked = pickedIds
        .map(id => byId.get(id))
        .filter((r): r is Recipe => !!r);
      if (picked.length === 3) return picked;
    }

    const shuffled = [...recipes].sort(() => Math.random() - 0.5).slice(0, 3);
    setPageCache(CTA_PICKS_CACHE_KEY, shuffled.map(r => r.id));
    return shuffled;
  }, [recipes]);

  
  if (isMobile) {
    // Mobile version with vertical layout
    return (
      <Card 
        radius="lg"
        p="md"
        style={{ 
          background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)',
          border: '1px solid #bae6fd',
          boxShadow: '0 1px 3px rgba(3, 105, 161, 0.08)'
        }}
      >
        <Group align="center" mb="sm">
          {/* Recipe photo as accent */}
          {selectedRecipes.length > 0 && (
            <Box 
              style={{ 
                width: 48,
                height: 48,
                borderRadius: '50%',
                overflow: 'hidden',
                flexShrink: 0,
                border: '2px solid #bae6fd',
                cursor: 'pointer'
              }}
              onClick={onViewCookbook}
            >
              <RecipeImage
                src={selectedRecipes[0].imageUrl || "/images/recipe-example-1.png"}
                alt={selectedRecipes[0].name}
                width="100%"
                height="100%"
              />
            </Box>
          )}
          
          <Box style={{ flex: 1 }}>
            <Text fw={600} size="sm" c="dark" mb={2}>
              Your recipe collection
            </Text>
            <Text size="xs" c="dimmed">
              {formatCount(recipeCount)} recipe{recipeCount !== 1 ? 's' : ''} ready to inspire your next meal plan
            </Text>
          </Box>
        </Group>
        
        <Button 
          size="sm"
          variant="light"
          color="blue"
          leftSection={<IconBook size={16} />}
          onClick={onViewCookbook}
          fullWidth
          style={{ 
            fontWeight: 600
          }}
        >
          View cookbook
        </Button>
      </Card>
    );
  }
  
  return (
    <Card 
      radius="xl"
      p={0}
      style={{ 
        overflow: 'hidden',
        background: 'white',
        position: 'relative',
        border: '1px solid rgba(0, 0, 0, 0.06)',
        transition: 'all 0.2s ease'
      }}
      className="premium-cta"
    >
      <Group gap={0} align="stretch">
        {/* Recipe Photos Collage */}
        <Box 
          style={{ 
            width: 200,
            height: 110,
            position: 'relative',
            overflow: 'hidden',
            flexShrink: 0,
            cursor: 'pointer'
          }}
          onClick={onViewCookbook}
        >
          {selectedRecipes.length > 0 && (
            <>
              {selectedRecipes.length === 1 ? (
                /* Single recipe - center it */
                <Box
                  style={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: 90,
                    height: 90,
                    borderRadius: '50%',
                    overflow: 'hidden',
                    border: '3px solid #bae6fd'
                  }}
                >
                  <RecipeImage
                    src={selectedRecipes[0].imageUrl || "/images/recipe-example-1.png"}
                    alt={selectedRecipes[0].name}
                    width="100%"
                    height="100%"
                    style={{ filter: 'brightness(1.05) contrast(1.1) saturate(1.1)' }}
                  />
                </Box>
              ) : selectedRecipes.length === 2 ? (
                /* Two recipes - split vertically */
                <>
                  <Box
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '50%',
                      height: '100%'
                    }}
                  >
                    <RecipeImage
                      src={selectedRecipes[0].imageUrl || "/images/recipe-example-1.png"}
                      alt={selectedRecipes[0].name}
                      width="100%"
                      height="100%"
                      style={{ filter: 'brightness(1.05) contrast(1.1) saturate(1.1)' }}
                    />
                  </Box>
                  <Box
                    style={{
                      position: 'absolute',
                      top: 0,
                      right: 0,
                      width: '50%',
                      height: '100%'
                    }}
                  >
                    <RecipeImage
                      src={selectedRecipes[1].imageUrl || "/images/recipe-example-2.png"}
                      alt={selectedRecipes[1].name}
                      width="100%"
                      height="100%"
                      style={{ filter: 'brightness(1.05) contrast(1.1) saturate(1.1)' }}
                    />
                  </Box>
                </>
              ) : (
                /* Three or more recipes - original layout */
                <>
                  {/* Main photo */}
                  <Box
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: 115,
                      height: '100%'
                    }}
                  >
                    <RecipeImage
                      src={selectedRecipes[0].imageUrl || "/images/recipe-example-1.png"}
                      alt={selectedRecipes[0].name}
                      width="100%"
                      height="100%"
                      style={{ filter: 'brightness(1.05) contrast(1.1) saturate(1.1)' }}
                    />
                  </Box>
                  
                  {/* Top right photo */}
                  <Box
                    style={{
                      position: 'absolute',
                      top: 0,
                      right: 0,
                      width: 85,
                      height: 55
                    }}
                  >
                    <RecipeImage
                      src={selectedRecipes[1].imageUrl || "/images/recipe-example-2.png"}
                      alt={selectedRecipes[1].name}
                      width="100%"
                      height="100%"
                      style={{ filter: 'brightness(1.05) contrast(1.1) saturate(1.1)' }}
                    />
                  </Box>
                  
                  {/* Bottom right photo */}
                  <Box
                    style={{
                      position: 'absolute',
                      bottom: 0,
                      right: 0,
                      width: 85,
                      height: 55
                    }}
                  >
                    <RecipeImage
                      src={selectedRecipes[2].imageUrl || "/images/recipe-example-3.png"}
                      alt={selectedRecipes[2].name}
                      width="100%"
                      height="100%"
                      style={{ filter: 'brightness(1.05) contrast(1.1) saturate(1.1)' }}
                    />
                  </Box>
                </>
              )}
            </>
          )}

          {/* Premium overlay gradient */}
          <Box
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'linear-gradient(90deg, transparent 50%, rgba(0,0,0,0.03) 80%, rgba(255,255,255,0.1) 100%)',
              pointerEvents: 'none'
            }}
          />
        </Box>

        {/* Content Section */}
        <Box style={{ 
          flex: 1, 
          padding: isDesktop ? 'var(--mantine-spacing-lg)' : 'var(--mantine-spacing-md)',
          background: 'linear-gradient(135deg, rgba(255,255,255,0.95) 0%, rgba(248,250,252,0.95) 100%)'
        }}>
          <Group justify="space-between" align="center" h="100%">
            <Box style={{ flex: 1 }}>
              <Text 
                fw={700} 
                size={isDesktop ? "md" : "sm"}
                c="dark" 
                mb={isDesktop ? 6 : 4}
                style={{ 
                  letterSpacing: '-0.01em',
                  lineHeight: 1.2
                }}
              >
                Your recipe collection
              </Text>
              <Text 
                size={isDesktop ? "sm" : "xs"}
                c="dimmed" 
                style={{ 
                  lineHeight: isDesktop ? 1.4 : 1.3,
                  color: 'rgba(0,0,0,0.65)'
                }}
              >
                {formatCount(recipeCount)} recipe{recipeCount !== 1 ? 's' : ''} ready to inspire your next meal plan
              </Text>
            </Box>
            
            <Button 
              size={isDesktop ? "md" : "sm"}
              variant="gradient"
              gradient={{ from: 'blue', to: 'cyan', deg: 45 }}
              leftSection={<IconBook size={isDesktop ? 16 : 14} />}
              onClick={onViewCookbook}
              style={{ 
                flexShrink: 0,
                marginLeft: isDesktop ? 'var(--mantine-spacing-lg)' : 'var(--mantine-spacing-md)',
                fontWeight: 600,
                letterSpacing: '0.01em',
                boxShadow: '0 2px 8px rgba(59, 130, 246, 0.25)',
                border: 'none'
              }}
            >
              View cookbook
            </Button>
          </Group>
        </Box>
      </Group>
    </Card>
  );
};
